import * as React from 'react';

/**
 * Like React.SetStateAction<S>, but allows partial objects.
 */
export type MergeOrUpdateStateAction<S> = Partial<S> | ((prevState: S) => S);

/**
 * This acts just like `React.useState`, except that it returns an updater function that can
 * accept a partial state object and merge it in (like class-based components), instead of just overwriting it.
 *
 * In order to use the merge capability (not a primitive or function), the state must be an object.
 * If the state is a primitive, updating the state will overwrite its value.
 *
 * **CAUTION:** This pattern is provided for maximum flexibility with shared components. Consider the
 * following idiomatic alternatives before deciding that this hook is the best way to manage your use case.
 * - If you have multiple *independent* pieces of state, store them via separate `useState` hooks.
 * - If you have multiple *related* pieces of state, consider modeling them as an object but using
 *   `useReducer` to update them like a state machine.
 * - If you are using a piece of framework that exposes a single mergeable state variable (e.g.
 *   `ReactActionDialog` or `useInlineEditor`), but your state actually contains multiple related
 *   pieces, consider using the `useSetStateAsReducer` hook to allow updating it like a state machine.
 */
export function useMergeableState<S>(initialState: S): [ S, React.Dispatch<MergeOrUpdateStateAction<S>> ] {
  const [state, setState] = React.useState<S>(initialState);

  const merge: React.Dispatch<MergeOrUpdateStateAction<S>> = React.useCallback(
    (newState: MergeOrUpdateStateAction<S>) => setState(setStateArgForMergeableState(newState)),
    [setState]
  );

  return [ state, merge ];
}

/**
 * Converts the `setState` argument for a mergeable state to one that can be sent to the normal `setState`.
 */
export function setStateArgForMergeableState<S>(newState: MergeOrUpdateStateAction<S>): React.SetStateAction<S> {
  if (typeof newState === 'object') {
    return prev => ({
      ...prev,
      ...newState,
    });
  } else { // function or primitive
    return newState;
  }
}
